/**
 * Localized Meta SEO for Country Landing Pages
 * Provides language-aware titles, descriptions per country
 * Falls back to English if no translation exists
 */
import type { CountrySEO, CountrySEOMap } from './country-seo-i18n-types';
import { COUNTRY_SEO_I18N_WAVE2 } from './country-seo-i18n-wave2';

export type { CountrySEO, CountrySEOMap };

/**
 * Priority countries with full 10-language SEO coverage
 * Format: countryKey -> languageCode -> { title, description, heroTitle, heroSubtitle }
 */
export const COUNTRY_SEO_I18N: CountrySEOMap = {

  morocco: {
    fr: {
      title: 'Centre d\'Appel Maroc 2026 | Call Center Maroc N°1 | Call IT Dev',
      description: 'Centre d\'appel au Maroc : services BPO multilingue, support client 12+ langues, inbound/outbound 24/7. 500+ agents à Casablanca, CSAT 95%, dès 3 $/h. Pilote gratuit !',
      heroTitle: 'Centre d\'Appel au Maroc — Votre Partenaire BPO N°1',
      heroSubtitle: 'Le Maroc, hub mondial du BPO nearshore. Agents multilingues natifs, fuseau CET, 50-70% d\'économies vs l\'Europe. Pilote gratuit 2 semaines.',
    },
    es: {
      title: 'Call Center Marruecos 2026 | Centro de Llamadas N°1 | Call IT Dev',
      description: 'Centro de llamadas en Marruecos: BPO multilingüe 12+ idiomas, soporte 24/7, CSAT 95%, desde 3 $/h. Prueba piloto gratuita.',
      heroTitle: 'Centro de Llamadas en Marruecos — Su Socio BPO N°1',
      heroSubtitle: 'Marruecos, hub mundial de BPO nearshore. Agentes nativos multilingües, zona CET, 50-70% ahorro vs Europa.',
    },
    de: {
      title: 'Call Center Marokko 2026 | Callcenter Marokko Nr. 1 | Call IT Dev',
      description: 'Callcenter in Marokko: mehrsprachiger BPO 12+ Sprachen, 24/7-Support, CSAT 95%, ab 3 $/h. Kostenloser Pilottest.',
      heroTitle: 'Callcenter in Marokko — Ihr BPO-Partner Nr. 1',
      heroSubtitle: 'Marokko, weltweiter Nearshore-BPO-Hub. Muttersprachliche Agenten, CET-Zeitzone, 50-70% Ersparnis vs Europa.',
    },
    ar: {
      title: 'مركز اتصال المغرب 2026 | أفضل مركز اتصال | Call IT Dev',
      description: 'مركز اتصال في المغرب: خدمات BPO متعددة اللغات، 12+ لغة، دعم 24/7، CSAT 95%، من 3 $/ساعة. تجربة مجانية لمدة أسبوعين.',
      heroTitle: 'مركز اتصال في المغرب — شريكك الأول في خدمات BPO',
      heroSubtitle: 'المغرب، المركز العالمي لخدمات BPO. وكلاء متعددو اللغات، نفس المنطقة الزمنية الأوروبية، توفير 50-70%.',
    },
    it: {
      title: 'Call Center Marocco 2026 | Centro Chiamate N°1 | Call IT Dev',
      description: 'Call center in Marocco: BPO multilingue 12+ lingue, supporto 24/7, CSAT 95%, da 3 $/h. Prova pilota gratuita.',
      heroTitle: 'Call Center in Marocco — Il Tuo Partner BPO N°1',
      heroSubtitle: 'Il Marocco, hub mondiale del BPO nearshore. Agenti madrelingua, fuso CET, 50-70% di risparmio vs Europa.',
    },
    pt: {
      title: 'Call Center Marrocos 2026 | Central de Atendimento N°1 | Call IT Dev',
      description: 'Call center no Marrocos: BPO multilíngue 12+ idiomas, suporte 24/7, CSAT 95%, a partir de 3 $/h. Teste piloto gratuito.',
      heroTitle: 'Call Center no Marrocos — Seu Parceiro BPO N°1',
      heroSubtitle: 'Marrocos, hub mundial de BPO nearshore. Agentes nativos multilíngues, fuso CET, 50-70% de economia vs Europa.',
    },
    nl: {
      title: 'Callcenter Marokko 2026 | Contactcenter Nr. 1 | Call IT Dev',
      description: 'Callcenter in Marokko: meertalige BPO 12+ talen, 24/7 support, CSAT 95%, vanaf 3 $/u. Gratis pilot.',
      heroTitle: 'Callcenter in Marokko — Uw BPO-Partner Nr. 1',
      heroSubtitle: 'Marokko, wereldwijde nearshore BPO-hub. Moedertaalsprekers, CET-tijdzone, 50-70% besparing vs Europa.',
    },
    pl: {
      title: 'Call Center Maroko 2026 | Centrum Obsługi Nr 1 | Call IT Dev',
      description: 'Call center w Maroku: wielojęzyczny BPO 12+ języków, wsparcie 24/7, CSAT 95%, od 3 $/h. Bezpłatny pilot.',
      heroTitle: 'Call Center w Maroku — Twój Partner BPO Nr 1',
      heroSubtitle: 'Maroko, światowe centrum BPO nearshore. Agenci natywni, strefa CET, 50-70% oszczędności vs Europa.',
    },
    tr: {
      title: 'Fas Çağrı Merkezi 2026 | 1 Numaralı Çağrı Merkezi | Call IT Dev',
      description: 'Fas\'ta çağrı merkezi: çok dilli BPO 12+ dil, 7/24 destek, CSAT %95, 3 $/saatten başlayan fiyatlar. Ücretsiz pilot.',
      heroTitle: 'Fas\'ta Çağrı Merkezi — 1 Numaralı BPO Ortağınız',
      heroSubtitle: 'Fas, dünya çapında nearshore BPO merkezi. Anadil konuşanlar, CET saat dilimi, Avrupa\'ya göre %50-70 tasarruf.',
    },
    sv: {
      title: 'Callcenter Marocko 2026 | Kontaktcenter Nr 1 | Call IT Dev',
      description: 'Callcenter i Marocko: flerspråkig BPO 12+ språk, 24/7 support, CSAT 95%, från 3 $/h. Gratis pilottest.',
      heroTitle: 'Callcenter i Marocko — Din BPO-Partner Nr 1',
      heroSubtitle: 'Marocko, global nearshore BPO-hub. Modersmålstalare, CET-tidszon, 50-70% besparing vs Europa.',
    },
    da: {
      title: 'Callcenter Marokko 2026 | Kontaktcenter Nr. 1 | Call IT Dev',
      description: 'Callcenter i Marokko: flersproget BPO 12+ sprog, 24/7 support, CSAT 95%, fra 3 $/t. Gratis pilottest.',
      heroTitle: 'Callcenter i Marokko — Din BPO-Partner Nr. 1',
      heroSubtitle: 'Marokko, globalt nearshore BPO-center. Indfødte sprogtalere, CET-tidszone, 50-70% besparelse vs Europa.',
    },
    zh: {
      title: '摩洛哥呼叫中心 2026 | 第一BPO中心 | Call IT Dev',
      description: '摩洛哥呼叫中心：多语言BPO 12+语言，24/7支持，CSAT 95%，3美元/小时起。免费试点。',
      heroTitle: '摩洛哥呼叫中心 — 您的首选BPO合作伙伴',
      heroSubtitle: '摩洛哥，全球nearshore BPO中心。母语座席，CET时区，比欧洲节省50-70%。',
    },
    ja: {
      title: 'モロッココールセンター 2026 | No.1 BPOセンター | Call IT Dev',
      description: 'モロッコのコールセンター：多言語BPO 12+言語、24/7サポート、CSAT 95%、3$/時間〜。無料パイロット。',
      heroTitle: 'モロッコのコールセンター — No.1 BPOパートナー',
      heroSubtitle: 'モロッコ、グローバルなニアショアBPOハブ。ネイティブ話者、CET時間帯、欧州比50-70%削減。',
    },
    ko: {
      title: '모로코 콜센터 2026 | 1위 BPO 센터 | Call IT Dev',
      description: '모로코 콜센터: 다국어 BPO 12+ 언어, 24/7 지원, CSAT 95%, 시간당 3달러부터. 무료 파일럿.',
      heroTitle: '모로코 콜센터 — No.1 BPO 파트너',
      heroSubtitle: '모로코, 글로벌 니어쇼어 BPO 허브. 원어민 상담원, CET 시간대, 유럽 대비 50-70% 절감.',
    },
    ru: {
      title: 'Колл-центр Марокко 2026 | BPO-центр №1 | Call IT Dev',
      description: 'Колл-центр в Марокко: мультиязычный BPO 12+ языков, поддержка 24/7, CSAT 95%, от 3 $/ч. Бесплатный пилот.',
      heroTitle: 'Колл-центр в Марокко — Ваш BPO-партнер №1',
      heroSubtitle: 'Марокко — мировой nearshore BPO-хаб. Носители языка, часовой пояс CET, экономия 50-70% vs Европа.',
    },
  },

  france: {
    fr: {
      title: 'Centre d\'appel France | Externalisation Service Client 24/7 | Call IT Dev',
      description: 'Externalisez votre centre d\'appel en France : agents francophones natifs, fuseau CET, CSAT 95%, dès 3 $/h. 60% d\'économies vs Paris. Devis gratuit en 24h.',
      heroTitle: 'Centre d\'appel pour la France — Support francophone natif depuis Casablanca',
      heroSubtitle: 'Service client premium en français à tarif nearshore. Même fuseau horaire, même culture, 50-70% moins cher que Paris. Opérationnel en 2-4 semaines.',
    },
    es: {
      title: 'Call Center Francia | Externalización Atención al Cliente | Call IT Dev',
      description: 'Externalice su call center para Francia: agentes francófonos nativos, zona CET, CSAT 95%, desde 3 $/h. 60% ahorro vs París. Presupuesto gratis.',
      heroTitle: 'Call Center para Francia — Soporte Francófono Nativo desde Casablanca',
      heroSubtitle: 'Experiencia premium en francés a precios nearshore. Misma zona horaria, misma cultura, 50-70% menos que París.',
    },
    de: {
      title: 'Call Center Frankreich | Kundenservice Outsourcing 24/7 | Call IT Dev',
      description: 'Call Center für Frankreich auslagern: französische Muttersprachler, CET-Zeitzone, CSAT 95%, ab 3 $/h. 60% Ersparnis vs Paris.',
      heroTitle: 'Call Center für Frankreich — Französischer Muttersprachler-Support',
      heroSubtitle: 'Premium-Kundenservice auf Französisch zu Nearshore-Preisen. Gleiche Zeitzone, 50-70% günstiger als Paris.',
    },
    it: {
      title: 'Call Center Francia | Esternalizzazione Servizio Clienti | Call IT Dev',
      description: 'Esternalizza il tuo call center per la Francia: agenti francofoni madrelingua, fuso CET, CSAT 95%, da 3 $/h. 60% risparmio vs Parigi.',
      heroTitle: 'Call Center per la Francia — Supporto Francofono Nativo da Casablanca',
      heroSubtitle: 'Servizio clienti premium in francese a prezzi nearshore. Stesso fuso orario, 50-70% in meno rispetto a Parigi.',
    },
    pt: {
      title: 'Call Center França | Terceirização Atendimento ao Cliente | Call IT Dev',
      description: 'Terceirize seu call center para França: agentes francófonos nativos, fuso CET, CSAT 95%, a partir de 3 $/h. 60% economia vs Paris.',
      heroTitle: 'Call Center para França — Suporte Francófono Nativo de Casablanca',
      heroSubtitle: 'Atendimento premium em francês a preços nearshore. Mesmo fuso horário, 50-70% mais barato que Paris.',
    },
    nl: {
      title: 'Callcenter Frankrijk | Klantenservice Outsourcing 24/7 | Call IT Dev',
      description: 'Callcenter uitbesteden voor Frankrijk: Franstalige moedertaalsprekers, CET-tijdzone, CSAT 95%, vanaf 3 $/u. 60% besparing vs Parijs.',
      heroTitle: 'Callcenter voor Frankrijk — Franstalige Moedertaalsprekers vanuit Casablanca',
      heroSubtitle: 'Premium klantenservice in het Frans tegen nearshore-prijzen. Zelfde tijdzone, 50-70% goedkoper dan Parijs.',
    },
    pl: {
      title: 'Call Center Francja | Outsourcing Obsługi Klienta 24/7 | Call IT Dev',
      description: 'Outsourcing call center dla Francji: rodowici frankofonowie, strefa CET, CSAT 95%, od 3 $/h. 60% oszczędności vs Paryż.',
      heroTitle: 'Call Center dla Francji — Natywne Wsparcie Frankofońskie z Casablanki',
      heroSubtitle: 'Premiumowa obsługa klienta po francusku w cenach nearshore. Ta sama strefa czasowa, 50-70% taniej niż Paryż.',
    },
    tr: {
      title: 'Fransa Çağrı Merkezi | Müşteri Hizmetleri Dış Kaynak | Call IT Dev',
      description: 'Fransa için çağrı merkezi dış kaynak: anadil Fransızca konuşanlar, CET saat dilimi, CSAT %95, 3 $/saatten başlayan fiyatlar.',
      heroTitle: 'Fransa İçin Çağrı Merkezi — Kazablanka\'dan Anadil Fransızca Destek',
      heroSubtitle: 'Nearshore fiyatlarla Fransızca premium müşteri deneyimi. Aynı saat dilimi, Paris\'ten %50-70 daha ucuz.',
    },
    sv: {
      title: 'Callcenter Frankrike | Kundtjänst Outsourcing 24/7 | Call IT Dev',
      description: 'Outsourca callcenter för Frankrike: fransktalande modersmålstalare, CET-tidszon, CSAT 95%, från 3 $/h. 60% besparing vs Paris.',
      heroTitle: 'Callcenter för Frankrike — Fransktalande Modersmålsstöd från Casablanca',
      heroSubtitle: 'Premium kundtjänst på franska till nearshore-priser. Samma tidszon, 50-70% billigare än Paris.',
    },
    da: {
      title: 'Callcenter Frankrig | Kundeservice Outsourcing 24/7 | Call IT Dev',
      description: 'Outsource callcenter til Frankrig: fransktalende indfødte, CET-tidszone, CSAT 95%, fra 3 $/t. 60% besparelse vs Paris.',
      heroTitle: 'Callcenter til Frankrig — Fransktalende Support fra Casablanca',
      heroSubtitle: 'Premium kundeservice på fransk til nearshore-priser. Samme tidszone, 50-70% billigere end Paris.',
    },
    ar: {
      title: 'مركز اتصال فرنسا | خدمة عملاء بالفرنسية 24/7 | Call IT Dev',
      description: 'مركز اتصال لفرنسا: وكلاء فرنكوفونيون أصليون، منطقة CET، CSAT 95%، من 3 $/ساعة. توفير 60% مقارنة بباريس.',
      heroTitle: 'مركز اتصال لفرنسا — دعم فرنكوفوني أصلي من الدار البيضاء',
      heroSubtitle: 'خدمة عملاء متميزة بالفرنسية بأسعار nearshore. نفس المنطقة الزمنية، أقل 50-70% من باريس.',
    },
    zh: {
      title: '法国呼叫中心 | 法语客服外包 24/7 | Call IT Dev',
      description: '法国呼叫中心外包：法语母语座席，CET时区，CSAT 95%，3美元/小时起。比巴黎节省60%。',
      heroTitle: '法国呼叫中心 — 卡萨布兰卡法语母语支持',
      heroSubtitle: '以nearshore价格提供优质法语客户体验。相同时区，比巴黎运营成本低50-70%。',
    },
    ja: {
      title: 'フランスコールセンター | フランス語カスタマーサポート外注 | Call IT Dev',
      description: 'フランス向けコールセンター外注：フランス語ネイティブ、CET時間帯、CSAT 95%、3$/時間〜。パリ比60%削減。',
      heroTitle: 'フランス向けコールセンター — カサブランカからのフランス語ネイティブサポート',
      heroSubtitle: 'ニアショア価格でプレミアムなフランス語顧客体験を。同じタイムゾーン、パリの50-70%低コスト。',
    },
    ko: {
      title: '프랑스 콜센터 | 프랑스어 고객 서비스 아웃소싱 | Call IT Dev',
      description: '프랑스 콜센터 아웃소싱: 프랑스어 원어민 상담원, CET 시간대, CSAT 95%, 시간당 3달러부터. 파리 대비 60% 절감.',
      heroTitle: '프랑스 콜센터 — 카사블랑카에서 프랑스어 원어민 지원',
      heroSubtitle: '니어쇼어 가격으로 프리미엄 프랑스어 고객 경험을 제공합니다. 같은 시간대, 파리보다 50-70% 저렴.',
    },
    ru: {
      title: 'Колл-центр Франция | Аутсорсинг клиентского сервиса 24/7 | Call IT Dev',
      description: 'Колл-центр для Франции: франкоговорящие агенты, часовой пояс CET, CSAT 95%, от 3 $/ч. Экономия 60% vs Париж.',
      heroTitle: 'Колл-центр для Франции — Франкоязычная поддержка из Касабланки',
      heroSubtitle: 'Премиум-обслуживание клиентов на французском по nearshore-ценам. Тот же часовой пояс, на 50-70% дешевле Парижа.',
    },
  },

  germany: {
    fr: {
      title: 'Centre d\'appel Allemagne | Service Client Germanophone | Call IT Dev',
      description: 'Externalisez votre centre d\'appel pour l\'Allemagne : agents germanophones, fuseau CET, CSAT 95%, dès 10 €/h. 55% d\'économies vs Berlin.',
      heroTitle: 'Centre d\'appel pour l\'Allemagne — Support germanophone',
      heroSubtitle: 'Service client premium en allemand. Même fuseau horaire CET, 55% moins cher que Berlin.',
    },
    de: {
      title: 'Callcenter Deutschland | Kundenservice Outsourcing 24/7 | Call IT Dev',
      description: 'Callcenter Deutschland auslagern: deutschsprachige Agenten, CET-Zeitzone, CSAT 95%, ab 10 €/h. 55% Ersparnis vs Berlin. Jetzt Angebot anfordern!',
      heroTitle: 'Callcenter für Deutschland — Deutschsprachiger Kundenservice',
      heroSubtitle: 'Premium-Kundenservice auf Deutsch zu Nearshore-Preisen. Gleiche CET-Zeitzone, 55% günstiger als Berlin.',
    },
    es: {
      title: 'Call Center Alemania | Atención al Cliente en Alemán | Call IT Dev',
      description: 'Call center para Alemania: agentes germanófonos, zona CET, CSAT 95%, desde 10 €/h. 55% ahorro vs Berlín.',
      heroTitle: 'Call Center para Alemania — Soporte en Alemán',
      heroSubtitle: 'Atención al cliente premium en alemán a precios nearshore. 55% más barato que Berlín.',
    },
    it: {
      title: 'Call Center Germania | Assistenza Clienti in Tedesco | Call IT Dev',
      description: 'Call center per la Germania: agenti germanofoni, fuso CET, CSAT 95%, da 10 €/h. 55% risparmio vs Berlino.',
      heroTitle: 'Call Center per la Germania — Supporto in Tedesco',
      heroSubtitle: 'Assistenza clienti premium in tedesco a prezzi nearshore. 55% in meno rispetto a Berlino.',
    },
    pt: {
      title: 'Call Center Alemanha | Atendimento ao Cliente em Alemão | Call IT Dev',
      description: 'Call center para Alemanha: agentes germanófonos, fuso CET, CSAT 95%, a partir de 10 €/h. 55% economia vs Berlim.',
      heroTitle: 'Call Center para Alemanha — Suporte em Alemão',
      heroSubtitle: 'Atendimento premium em alemão a preços nearshore. 55% mais barato que Berlim.',
    },
    nl: {
      title: 'Callcenter Duitsland | Duitstalige Klantenservice | Call IT Dev',
      description: 'Callcenter voor Duitsland: Duitstalige agenten, CET-tijdzone, CSAT 95%, vanaf 10 €/u. 55% besparing vs Berlijn.',
      heroTitle: 'Callcenter voor Duitsland — Duitstalige Klantenservice',
      heroSubtitle: 'Premium klantenservice in het Duits tegen nearshore-prijzen. 55% goedkoper dan Berlijn.',
    },
    ar: {
      title: 'مركز اتصال ألمانيا | خدمة عملاء بالألمانية 24/7 | Call IT Dev',
      description: 'مركز اتصال لألمانيا: وكلاء ناطقون بالألمانية، منطقة CET، CSAT 95%، من 10 €/ساعة. توفير 55% مقارنة ببرلين.',
      heroTitle: 'مركز اتصال لألمانيا — دعم بالألمانية',
      heroSubtitle: 'خدمة عملاء متميزة بالألمانية بأسعار nearshore. أقل 55% من برلين.',
    },
    pl: {
      title: 'Call Center Niemcy | Obsługa Klienta po Niemiecku | Call IT Dev',
      description: 'Call center dla Niemiec: niemieckojęzyczni agenci, strefa CET, CSAT 95%, od 10 €/h. 55% oszczędności vs Berlin.',
      heroTitle: 'Call Center dla Niemiec — Niemieckojęzyczna Obsługa',
      heroSubtitle: 'Premiumowa obsługa klienta po niemiecku w cenach nearshore. 55% taniej niż Berlin.',
    },
    tr: {
      title: 'Almanya Çağrı Merkezi | Almanca Müşteri Hizmetleri | Call IT Dev',
      description: 'Almanya için çağrı merkezi: Almanca konuşan ajanlar, CET saat dilimi, CSAT %95, 10 €/saatten. Berlin\'e göre %55 tasarruf.',
      heroTitle: 'Almanya İçin Çağrı Merkezi — Almanca Destek',
      heroSubtitle: 'Nearshore fiyatlarla Almanca premium müşteri hizmeti. Berlin\'den %55 daha ucuz.',
    },
    sv: {
      title: 'Callcenter Tyskland | Tyskspråkig Kundtjänst | Call IT Dev',
      description: 'Callcenter för Tyskland: tyskspråkiga agenter, CET-tidszon, CSAT 95%, från 10 €/h. 55% besparing vs Berlin.',
      heroTitle: 'Callcenter för Tyskland — Tyskspråkig Kundtjänst',
      heroSubtitle: 'Premium kundtjänst på tyska till nearshore-priser. 55% billigare än Berlin.',
    },
    da: {
      title: 'Callcenter Tyskland | Tysksproget Kundeservice | Call IT Dev',
      description: 'Callcenter for Tyskland: tysksproget agenter, CET-tidszone, CSAT 95%, fra 10 €/t. 55% besparelse vs Berlin.',
      heroTitle: 'Callcenter for Tyskland — Tysksproget Kundeservice',
      heroSubtitle: 'Premium kundeservice på tysk til nearshore-priser. 55% billigere end Berlin.',
    },
    zh: {
      title: '德国呼叫中心 | 德语客服外包 | Call IT Dev',
      description: '德国呼叫中心外包：德语母语座席，CET时区，CSAT 95%，10€/小时起。比柏林节省55%。',
      heroTitle: '德国呼叫中心 — 德语母语支持',
      heroSubtitle: '以nearshore价格提供优质德语客户体验。比柏林运营成本低55%。',
    },
    ja: {
      title: 'ドイツコールセンター | ドイツ語カスタマーサポート | Call IT Dev',
      description: 'ドイツ向けコールセンター：ドイツ語ネイティブ、CET時間帯、CSAT 95%、10€/時間〜。ベルリン比55%削減。',
      heroTitle: 'ドイツ向けコールセンター — ドイツ語ネイティブサポート',
      heroSubtitle: 'ニアショア価格でプレミアムなドイツ語カスタマー体験。ベルリンの55%低コスト。',
    },
    ko: {
      title: '독일 콜센터 | 독일어 고객 서비스 | Call IT Dev',
      description: '독일 콜센터: 독일어 원어민 상담원, CET 시간대, CSAT 95%, 10€/시간부터. 베를린 대비 55% 절감.',
      heroTitle: '독일 콜센터 — 독일어 원어민 지원',
      heroSubtitle: '니어쇼어 가격으로 프리미엄 독일어 고객 경험. 베를린보다 55% 저렴.',
    },
    ru: {
      title: 'Колл-центр Германия | Немецкоязычный сервис 24/7 | Call IT Dev',
      description: 'Колл-центр для Германии: немецкоговорящие агенты, часовой пояс CET, CSAT 95%, от 10 €/ч. Экономия 55% vs Берлин.',
      heroTitle: 'Колл-центр для Германии — Немецкоязычная поддержка',
      heroSubtitle: 'Премиум-обслуживание на немецком по nearshore-ценам. На 55% дешевле Берлина.',
    },
  },

  spain: {
    fr: {
      title: 'Centre d\'appel Espagne | Service Client Hispanophone | Call IT Dev',
      description: 'Externalisez votre centre d\'appel pour l\'Espagne : agents hispanophones natifs, fuseau CET, CSAT 95%, dès 3 $/h. 55% d\'économies vs Madrid.',
      heroTitle: 'Centre d\'appel pour l\'Espagne — Support hispanophone natif',
      heroSubtitle: 'Service client premium en espagnol. Même fuseau horaire, 55% moins cher que Madrid.',
    },
    es: {
      title: 'Centro de Llamadas España | Externalización Atención al Cliente | Call IT Dev',
      description: 'Externalice su centro de llamadas en España: agentes hispanohablantes nativos, zona CET, CSAT 95%, desde 3 $/h. 55% ahorro vs Madrid. Presupuesto gratis.',
      heroTitle: 'Centro de Llamadas para España — Atención al Cliente Nativa',
      heroSubtitle: 'Servicio al cliente premium en español a precios nearshore. Misma zona horaria, 55% más barato que Madrid.',
    },
    de: {
      title: 'Call Center Spanien | Spanischsprachiger Kundenservice | Call IT Dev',
      description: 'Call Center für Spanien: spanische Muttersprachler, CET-Zeitzone, CSAT 95%, ab 3 $/h. 55% Ersparnis vs Madrid.',
      heroTitle: 'Call Center für Spanien — Spanischsprachiger Kundenservice',
      heroSubtitle: 'Premium-Kundenservice auf Spanisch zu Nearshore-Preisen. 55% günstiger als Madrid.',
    },
    it: {
      title: 'Call Center Spagna | Assistenza Clienti in Spagnolo | Call IT Dev',
      description: 'Call center per la Spagna: agenti ispanofoni madrelingua, fuso CET, CSAT 95%, da 3 $/h. 55% risparmio vs Madrid.',
      heroTitle: 'Call Center per la Spagna — Supporto in Spagnolo Nativo',
      heroSubtitle: 'Assistenza clienti premium in spagnolo a prezzi nearshore. 55% in meno rispetto a Madrid.',
    },
    pt: {
      title: 'Call Center Espanha | Atendimento ao Cliente em Espanhol | Call IT Dev',
      description: 'Call center para Espanha: agentes hispanófonos nativos, fuso CET, CSAT 95%, a partir de 3 $/h. 55% economia vs Madrid.',
      heroTitle: 'Call Center para Espanha — Suporte em Espanhol Nativo',
      heroSubtitle: 'Atendimento premium em espanhol a preços nearshore. 55% mais barato que Madrid.',
    },
    ar: {
      title: 'مركز اتصال إسبانيا | خدمة عملاء بالإسبانية 24/7 | Call IT Dev',
      description: 'مركز اتصال لإسبانيا: وكلاء ناطقون بالإسبانية، منطقة CET، CSAT 95%، من 3 $/ساعة. توفير 55% مقارنة بمدريد.',
      heroTitle: 'مركز اتصال لإسبانيا — دعم بالإسبانية',
      heroSubtitle: 'خدمة عملاء متميزة بالإسبانية بأسعار nearshore. أقل 55% من مدريد.',
    },
    nl: {
      title: 'Callcenter Spanje | Spaanstalige Klantenservice | Call IT Dev',
      description: 'Callcenter voor Spanje: Spaanstalige moedertaalsprekers, CET-tijdzone, CSAT 95%, vanaf 3 $/u. 55% besparing vs Madrid.',
      heroTitle: 'Callcenter voor Spanje — Spaanstalige Klantenservice',
      heroSubtitle: 'Premium klantenservice in het Spaans. 55% goedkoper dan Madrid.',
    },
    pl: {
      title: 'Call Center Hiszpania | Obsługa Klienta po Hiszpańsku | Call IT Dev',
      description: 'Call center dla Hiszpanii: hiszpańskojęzyczni agenci, strefa CET, CSAT 95%, od 3 $/h. 55% oszczędności vs Madryt.',
      heroTitle: 'Call Center dla Hiszpanii — Hiszpańskojęzyczna Obsługa',
      heroSubtitle: 'Premiumowa obsługa klienta po hiszpańsku. 55% taniej niż Madryt.',
    },
    tr: {
      title: 'İspanya Çağrı Merkezi | İspanyolca Müşteri Hizmetleri | Call IT Dev',
      description: 'İspanya için çağrı merkezi: İspanyolca anadil konuşanlar, CET saat dilimi, CSAT %95, 3 $/saatten. Madrid\'e göre %55 tasarruf.',
      heroTitle: 'İspanya İçin Çağrı Merkezi — İspanyolca Destek',
      heroSubtitle: 'Nearshore fiyatlarla İspanyolca premium müşteri hizmeti. Madrid\'den %55 daha ucuz.',
    },
    sv: {
      title: 'Callcenter Spanien | Spanskspråkig Kundtjänst | Call IT Dev',
      description: 'Callcenter för Spanien: spanskspråkiga modersmålstalare, CET-tidszon, CSAT 95%, från 3 $/h. 55% besparing vs Madrid.',
      heroTitle: 'Callcenter för Spanien — Spanskspråkig Kundtjänst',
      heroSubtitle: 'Premium kundtjänst på spanska. 55% billigare än Madrid.',
    },
    da: {
      title: 'Callcenter Spanien | Spansktalende Kundeservice | Call IT Dev',
      description: 'Callcenter for Spanien: spansktalende indfødte, CET-tidszone, CSAT 95%, fra 3 $/t. 55% besparelse vs Madrid.',
      heroTitle: 'Callcenter for Spanien — Spansktalende Kundeservice',
      heroSubtitle: 'Premium kundeservice på spansk. 55% billigere end Madrid.',
    },
    zh: {
      title: '西班牙呼叫中心 | 西班牙语客服外包 | Call IT Dev',
      description: '西班牙呼叫中心外包：西班牙语母语座席，CET时区，CSAT 95%，3美元/小时起。比马德里节省55%。',
      heroTitle: '西班牙呼叫中心 — 西班牙语母语支持',
      heroSubtitle: '以nearshore价格提供优质西班牙语客户体验。比马德里运营成本低55%。',
    },
    ja: {
      title: 'スペインコールセンター | スペイン語カスタマーサポート | Call IT Dev',
      description: 'スペイン向けコールセンター：スペイン語ネイティブ、CET時間帯、CSAT 95%、3$/時間〜。マドリード比55%削減。',
      heroTitle: 'スペイン向けコールセンター — スペイン語ネイティブサポート',
      heroSubtitle: 'ニアショア価格でプレミアムなスペイン語体験。マドリードの55%低コスト。',
    },
    ko: {
      title: '스페인 콜센터 | 스페인어 고객 서비스 | Call IT Dev',
      description: '스페인 콜센터: 스페인어 원어민, CET 시간대, CSAT 95%, 3$/시간부터. 마드리드 대비 55% 절감.',
      heroTitle: '스페인 콜센터 — 스페인어 원어민 지원',
      heroSubtitle: '니어쇼어 가격으로 프리미엄 스페인어 체험. 마드리드보다 55% 저렴.',
    },
    ru: {
      title: 'Колл-центр Испания | Испаноязычный сервис 24/7 | Call IT Dev',
      description: 'Колл-центр для Испании: испаноговорящие агенты, часовой пояс CET, CSAT 95%, от 3 $/ч. Экономия 55% vs Мадрид.',
      heroTitle: 'Колл-центр для Испании — Испаноязычная поддержка',
      heroSubtitle: 'Премиум-обслуживание на испанском по nearshore-ценам. На 55% дешевле Мадрида.',
    },
  },

  uk: {
    fr: {
      title: 'Centre d\'appel Royaume-Uni | Service Client Anglophone | Call IT Dev',
      description: 'Externalisez votre centre d\'appel pour le Royaume-Uni : agents anglophones, CSAT 95%, dès 3 $/h. 65% d\'économies vs Londres.',
      heroTitle: 'Centre d\'appel pour le Royaume-Uni — Support anglophone',
      heroSubtitle: 'Service client premium en anglais. 65% moins cher que Londres.',
    },
    es: {
      title: 'Call Center Reino Unido | Atención al Cliente en Inglés | Call IT Dev',
      description: 'Call center para Reino Unido: agentes anglófonos, CSAT 95%, desde 3 $/h. 65% ahorro vs Londres.',
      heroTitle: 'Call Center para Reino Unido — Soporte Anglófono',
      heroSubtitle: 'Atención al cliente premium en inglés a precios nearshore. 65% más barato que Londres.',
    },
    de: {
      title: 'Call Center Großbritannien | Englischsprachiger Kundenservice | Call IT Dev',
      description: 'Call Center für UK: englischsprachige Agenten, CSAT 95%, ab 3 $/h. 65% Ersparnis vs London.',
      heroTitle: 'Call Center für Großbritannien — Englischsprachiger Support',
      heroSubtitle: 'Premium-Kundenservice auf Englisch zu Nearshore-Preisen. 65% günstiger als London.',
    },
    ar: {
      title: 'مركز اتصال المملكة المتحدة | خدمة عملاء بالإنجليزية 24/7 | Call IT Dev',
      description: 'مركز اتصال للمملكة المتحدة: وكلاء ناطقون بالإنجليزية، CSAT 95%، من 3 $/ساعة. توفير 65% مقارنة بلندن.',
      heroTitle: 'مركز اتصال للمملكة المتحدة — دعم بالإنجليزية',
      heroSubtitle: 'خدمة عملاء متميزة بالإنجليزية. أقل 65% من لندن.',
    },
    it: {
      title: 'Call Center Regno Unito | Assistenza Clienti in Inglese | Call IT Dev',
      description: 'Call center per il Regno Unito: agenti anglofoni, CSAT 95%, da 3 $/h. 65% risparmio vs Londra.',
      heroTitle: 'Call Center per il Regno Unito — Supporto in Inglese',
      heroSubtitle: 'Assistenza clienti premium in inglese. 65% in meno rispetto a Londra.',
    },
    pt: {
      title: 'Call Center Reino Unido | Atendimento em Inglês | Call IT Dev',
      description: 'Call center para Reino Unido: agentes anglófonos, CSAT 95%, a partir de 3 $/h. 65% economia vs Londres.',
      heroTitle: 'Call Center para Reino Unido — Suporte em Inglês',
      heroSubtitle: 'Atendimento premium em inglês. 65% mais barato que Londres.',
    },
    nl: {
      title: 'Callcenter Verenigd Koninkrijk | Engelstalige Klantenservice | Call IT Dev',
      description: 'Callcenter voor UK: Engelstalige agenten, CSAT 95%, vanaf 3 $/u. 65% besparing vs Londen.',
      heroTitle: 'Callcenter voor het Verenigd Koninkrijk — Engelstalige Support',
      heroSubtitle: 'Premium klantenservice in het Engels. 65% goedkoper dan Londen.',
    },
    pl: {
      title: 'Call Center Wielka Brytania | Obsługa po Angielsku | Call IT Dev',
      description: 'Call center dla Wielkiej Brytanii: anglojęzyczni agenci, CSAT 95%, od 3 $/h. 65% oszczędności vs Londyn.',
      heroTitle: 'Call Center dla Wielkiej Brytanii — Anglojęzyczna Obsługa',
      heroSubtitle: 'Premiumowa obsługa klienta po angielsku. 65% taniej niż Londyn.',
    },
    tr: {
      title: 'İngiltere Çağrı Merkezi | İngilizce Müşteri Hizmetleri | Call IT Dev',
      description: 'İngiltere için çağrı merkezi: İngilizce konuşan ajanlar, CSAT %95, 3 $/saatten. Londra\'ya göre %65 tasarruf.',
      heroTitle: 'İngiltere İçin Çağrı Merkezi — İngilizce Destek',
      heroSubtitle: 'Nearshore fiyatlarla İngilizce premium müşteri hizmeti. Londra\'dan %65 daha ucuz.',
    },
    sv: {
      title: 'Callcenter Storbritannien | Engelskspråkig Kundtjänst | Call IT Dev',
      description: 'Callcenter för UK: engelskspråkiga agenter, CSAT 95%, från 3 $/h. 65% besparing vs London.',
      heroTitle: 'Callcenter för Storbritannien — Engelskspråkig Kundtjänst',
      heroSubtitle: 'Premium kundtjänst på engelska. 65% billigare än London.',
    },
    da: {
      title: 'Callcenter Storbritannien | Engelsksproget Kundeservice | Call IT Dev',
      description: 'Callcenter for UK: engelsksproget agenter, CSAT 95%, fra 3 $/t. 65% besparelse vs London.',
      heroTitle: 'Callcenter for Storbritannien — Engelsksproget Kundeservice',
      heroSubtitle: 'Premium kundeservice på engelsk. 65% billigere end London.',
    },
    zh: {
      title: '英国呼叫中心 | 英语客服外包 | Call IT Dev',
      description: '英国呼叫中心外包：英语母语座席，CSAT 95%，3美元/小时起。比伦敦节省65%。',
      heroTitle: '英国呼叫中心 — 英语母语支持',
      heroSubtitle: '以nearshore价格提供优质英语客户体验。比伦敦运营成本低65%。',
    },
    ja: {
      title: 'イギリスコールセンター | 英語カスタマーサポート外注 | Call IT Dev',
      description: 'イギリス向けコールセンター：英語ネイティブ、CSAT 95%、3$/時間〜。ロンドン比65%削減。',
      heroTitle: 'イギリス向けコールセンター — 英語ネイティブサポート',
      heroSubtitle: 'ニアショア価格でプレミアムな英語カスタマー体験。ロンドンの65%低コスト。',
    },
    ko: {
      title: '영국 콜센터 | 영어 고객 서비스 아웃소싱 | Call IT Dev',
      description: '영국 콜센터: 영어 원어민 상담원, CSAT 95%, 3$/시간부터. 런던 대비 65% 절감.',
      heroTitle: '영국 콜센터 — 영어 원어민 지원',
      heroSubtitle: '니어쇼어 가격으로 프리미엄 영어 고객 경험. 런던보다 65% 저렴.',
    },
    ru: {
      title: 'Колл-центр Великобритания | Англоязычный сервис 24/7 | Call IT Dev',
      description: 'Колл-центр для UK: англоязычные агенты, CSAT 95%, от 3 $/ч. Экономия 65% vs Лондон.',
      heroTitle: 'Колл-центр для Великобритании — Англоязычная поддержка',
      heroSubtitle: 'Премиум-обслуживание на английском. На 65% дешевле Лондона.',
    },
  },

  usa: {
    fr: {
      title: 'Centre d\'appel États-Unis | Service Client Anglophone 24/7 | Call IT Dev',
      description: 'Externalisez votre centre d\'appel pour les USA : agents anglophones, couverture 24/7, CSAT 95%, dès 3 $/h. 70% d\'économies vs New York.',
      heroTitle: 'Centre d\'appel pour les États-Unis — Support anglophone 24/7',
      heroSubtitle: 'Service client premium pour le marché américain. 70% moins cher que New York.',
    },
    es: {
      title: 'Call Center Estados Unidos | Atención al Cliente 24/7 | Call IT Dev',
      description: 'Call center para EEUU: agentes bilingües inglés/español, cobertura 24/7, CSAT 95%, desde 3 $/h. 70% ahorro.',
      heroTitle: 'Call Center para Estados Unidos — Soporte Bilingüe 24/7',
      heroSubtitle: 'Atención al cliente premium para el mercado estadounidense. 70% más barato que Nueva York.',
    },
    de: {
      title: 'Call Center USA | Englischsprachiger Kundenservice 24/7 | Call IT Dev',
      description: 'Call Center für USA: englischsprachige Agenten, 24/7-Abdeckung, CSAT 95%, ab 3 $/h. 70% Ersparnis vs New York.',
      heroTitle: 'Call Center für die USA — Englischsprachiger Support 24/7',
      heroSubtitle: 'Premium-Kundenservice für den US-Markt. 70% günstiger als New York.',
    },
    ar: {
      title: 'مركز اتصال الولايات المتحدة | خدمة عملاء 24/7 | Call IT Dev',
      description: 'مركز اتصال لأمريكا: وكلاء ثنائيو اللغة، تغطية 24/7، CSAT 95%، من 3 $/ساعة. توفير 70%.',
      heroTitle: 'مركز اتصال للولايات المتحدة — دعم ثنائي اللغة 24/7',
      heroSubtitle: 'خدمة عملاء متميزة للسوق الأمريكي. أقل 70% من نيويورك.',
    },
  },

  uae: {
    fr: {
      title: 'Centre d\'appel EAU Dubai | Service Client Multilingue | Call IT Dev',
      description: 'Centre d\'appel pour les Émirats : agents arabophones et anglophones, CSAT 95%, dès 3 $/h. Support multilingue 24/7.',
      heroTitle: 'Centre d\'appel pour les EAU — Support multilingue depuis Casablanca',
      heroSubtitle: 'Service client premium en arabe et anglais pour le marché émirati.',
    },
    ar: {
      title: 'مركز اتصال الإمارات | خدمة عملاء متعددة اللغات 24/7 | Call IT Dev',
      description: 'مركز اتصال للإمارات: وكلاء عرب وإنجليز، CSAT 95%، من 3 $/ساعة. دعم متعدد اللغات 24/7 من الدار البيضاء.',
      heroTitle: 'مركز اتصال للإمارات — دعم متعدد اللغات من الدار البيضاء',
      heroSubtitle: 'خدمة عملاء متميزة بالعربية والإنجليزية للسوق الإماراتي. توفير 60%.',
    },
    de: {
      title: 'Call Center VAE Dubai | Mehrsprachiger Kundenservice | Call IT Dev',
      description: 'Call Center für VAE: arabisch- und englischsprachige Agenten, CSAT 95%, ab 3 $/h. 24/7 mehrsprachiger Support.',
      heroTitle: 'Call Center für die VAE — Mehrsprachiger Support',
      heroSubtitle: 'Premium-Kundenservice auf Arabisch und Englisch für den Emirate-Markt.',
    },
    es: {
      title: 'Call Center EAU Dubai | Servicio al Cliente Multilingüe | Call IT Dev',
      description: 'Call center para EAU: agentes árabe e inglés, CSAT 95%, desde 3 $/h. Soporte multilingüe 24/7.',
      heroTitle: 'Call Center para EAU — Soporte Multilingüe desde Casablanca',
      heroSubtitle: 'Atención al cliente premium en árabe e inglés para el mercado emiratí.',
    },
  },

  'saudi-arabia': {
    ar: {
      title: 'مركز اتصال المملكة العربية السعودية | خدمة عملاء 24/7 | Call IT Dev',
      description: 'مركز اتصال للسعودية: وكلاء عرب أصليون، CSAT 95%، من 3 $/ساعة. دعم 24/7 متوافق مع رؤية 2030.',
      heroTitle: 'مركز اتصال للمملكة العربية السعودية — دعم عربي أصلي',
      heroSubtitle: 'خدمة عملاء متميزة بالعربية للسوق السعودي. متوافق مع رؤية 2030.',
    },
    fr: {
      title: 'Centre d\'appel Arabie Saoudite | Service Client Arabophone | Call IT Dev',
      description: 'Centre d\'appel pour l\'Arabie Saoudite : agents arabophones natifs, CSAT 95%, dès 3 $/h. Support 24/7 Vision 2030.',
      heroTitle: 'Centre d\'appel pour l\'Arabie Saoudite — Support arabophone',
      heroSubtitle: 'Service client premium en arabe pour le marché saoudien.',
    },
  },

  canada: {
    fr: {
      title: 'Centre d\'appel Canada | Service Client Bilingue FR/EN | Call IT Dev',
      description: 'Centre d\'appel pour le Canada : agents bilingues français/anglais, CSAT 95%, dès 3 $/h. 65% d\'économies vs Montréal.',
      heroTitle: 'Centre d\'appel pour le Canada — Support bilingue FR/EN',
      heroSubtitle: 'Service client bilingue français-anglais pour le marché canadien. 65% moins cher que Montréal.',
    },
    es: {
      title: 'Call Center Canadá | Atención al Cliente Bilingüe FR/EN | Call IT Dev',
      description: 'Call center para Canadá: agentes bilingües francés/inglés, CSAT 95%, desde 3 $/h. 65% ahorro vs Montreal.',
      heroTitle: 'Call Center para Canadá — Soporte Bilingüe FR/EN',
      heroSubtitle: 'Atención al cliente bilingüe para el mercado canadiense.',
    },
  },

  netherlands: {
    nl: {
      title: 'Callcenter Nederland | Klantenservice Outsourcing 24/7 | Call IT Dev',
      description: 'Callcenter uitbesteden voor Nederland: meertalige agenten, CSAT 95%, vanaf 3 $/u. 55% besparing vs Amsterdam.',
      heroTitle: 'Callcenter voor Nederland — Meertalige Klantenservice',
      heroSubtitle: 'Premium klantenservice voor de Nederlandse markt. 55% goedkoper dan Amsterdam.',
    },
    fr: {
      title: 'Centre d\'appel Pays-Bas | Service Client Multilingue | Call IT Dev',
      description: 'Centre d\'appel pour les Pays-Bas : agents multilingues, CSAT 95%, dès 3 $/h. 55% d\'économies vs Amsterdam.',
      heroTitle: 'Centre d\'appel pour les Pays-Bas — Support multilingue',
      heroSubtitle: 'Service client premium pour le marché néerlandais.',
    },
  },

  belgium: {
    fr: {
      title: 'Centre d\'appel Belgique | Service Client FR/NL/DE | Call IT Dev',
      description: 'Centre d\'appel pour la Belgique : agents trilingues FR/NL/DE, CSAT 95%, dès 3 $/h. 55% d\'économies vs Bruxelles.',
      heroTitle: 'Centre d\'appel pour la Belgique — Support trilingue FR/NL/DE',
      heroSubtitle: 'Service client premium trilingue pour le marché belge. 55% moins cher que Bruxelles.',
    },
    nl: {
      title: 'Callcenter België | Drietalige Klantenservice FR/NL/DE | Call IT Dev',
      description: 'Callcenter voor België: drietalige agenten FR/NL/DE, CSAT 95%, vanaf 3 $/u. 55% besparing vs Brussel.',
      heroTitle: 'Callcenter voor België — Drietalige Klantenservice',
      heroSubtitle: 'Premium klantenservice voor de Belgische markt. 55% goedkoper dan Brussel.',
    },
    de: {
      title: 'Call Center Belgien | Dreisprachiger Kundenservice | Call IT Dev',
      description: 'Call Center für Belgien: dreisprachige Agenten FR/NL/DE, CSAT 95%, ab 3 $/h. 55% Ersparnis vs Brüssel.',
      heroTitle: 'Call Center für Belgien — Dreisprachiger Kundenservice',
      heroSubtitle: 'Premium-Kundenservice für den belgischen Markt.',
    },
  },

  switzerland: {
    fr: {
      title: 'Centre d\'appel Suisse | Service Client FR/DE/IT | Call IT Dev',
      description: 'Centre d\'appel pour la Suisse : agents trilingues FR/DE/IT, CSAT 95%, dès 8 €/h. 60% d\'économies vs Zurich.',
      heroTitle: 'Centre d\'appel pour la Suisse — Support trilingue FR/DE/IT',
      heroSubtitle: 'Service client premium trilingue pour le marché suisse. 60% moins cher que Zurich.',
    },
    de: {
      title: 'Call Center Schweiz | Dreisprachiger Kundenservice FR/DE/IT | Call IT Dev',
      description: 'Call Center für Schweiz: dreisprachige Agenten FR/DE/IT, CSAT 95%, ab 8 €/h. 60% Ersparnis vs Zürich.',
      heroTitle: 'Call Center für die Schweiz — Dreisprachiger Kundenservice',
      heroSubtitle: 'Premium-Kundenservice für den Schweizer Markt. 60% günstiger als Zürich.',
    },
    it: {
      title: 'Call Center Svizzera | Assistenza Clienti FR/DE/IT | Call IT Dev',
      description: 'Call center per la Svizzera: agenti trilingui FR/DE/IT, CSAT 95%, da 8 €/h. 60% risparmio vs Zurigo.',
      heroTitle: 'Call Center per la Svizzera — Assistenza Trilingue FR/DE/IT',
      heroSubtitle: 'Assistenza clienti premium per il mercato svizzero. 60% in meno rispetto a Zurigo.',
    },
  },

  italy: {
    it: {
      title: 'Call Center Italia | Esternalizzazione Servizio Clienti 24/7 | Call IT Dev',
      description: 'Esternalizza il tuo call center per l\'Italia: agenti italofoni, CSAT 95%, da 3 $/h. 55% risparmio vs Milano. Preventivo gratuito.',
      heroTitle: 'Call Center per l\'Italia — Supporto Italofono da Casablanca',
      heroSubtitle: 'Servizio clienti premium in italiano a prezzi nearshore. 55% in meno rispetto a Milano.',
    },
    fr: {
      title: 'Centre d\'appel Italie | Service Client Italien | Call IT Dev',
      description: 'Centre d\'appel pour l\'Italie : agents italophones, CSAT 95%, dès 3 $/h. 55% d\'économies vs Milan.',
      heroTitle: 'Centre d\'appel pour l\'Italie — Support italien',
      heroSubtitle: 'Service client premium en italien. 55% moins cher que Milan.',
    },
  },

  portugal: {
    pt: {
      title: 'Call Center Portugal | Terceirização Atendimento ao Cliente | Call IT Dev',
      description: 'Call center para Portugal: agentes lusófonos, CSAT 95%, a partir de 3 $/h. 50% economia vs Lisboa. Orçamento gratuito.',
      heroTitle: 'Call Center para Portugal — Suporte Lusófono',
      heroSubtitle: 'Atendimento ao cliente premium em português. 50% mais barato que Lisboa.',
    },
    fr: {
      title: 'Centre d\'appel Portugal | Service Client Lusophone | Call IT Dev',
      description: 'Centre d\'appel pour le Portugal : agents lusophones, CSAT 95%, dès 3 $/h. 50% d\'économies vs Lisbonne.',
      heroTitle: 'Centre d\'appel pour le Portugal — Support lusophone',
      heroSubtitle: 'Service client premium en portugais. 50% moins cher que Lisbonne.',
    },
  },

  poland: {
    pl: {
      title: 'Call Center Polska | Outsourcing Obsługi Klienta 24/7 | Call IT Dev',
      description: 'Call center dla Polski: wielojęzyczni agenci, CSAT 95%, od 3 $/h. 50% oszczędności vs Warszawa. Bezpłatna wycena.',
      heroTitle: 'Call Center dla Polski — Wielojęzyczna Obsługa Klienta',
      heroSubtitle: 'Premium obsługa klienta dla rynku polskiego. 50% taniej niż Warszawa.',
    },
    fr: {
      title: 'Centre d\'appel Pologne | Service Client Multilingue | Call IT Dev',
      description: 'Centre d\'appel pour la Pologne : agents multilingues, CSAT 95%, dès 3 $/h. 50% d\'économies vs Varsovie.',
      heroTitle: 'Centre d\'appel pour la Pologne — Support multilingue',
      heroSubtitle: 'Service client premium pour le marché polonais.',
    },
  },

  turkey: {
    tr: {
      title: 'Türkiye Çağrı Merkezi | Müşteri Hizmetleri Dış Kaynak 24/7 | Call IT Dev',
      description: 'Türkiye için çağrı merkezi: Türkçe konuşan agentlar, CSAT %95, 3 $/saatten başlayan fiyatlar. İstanbul\'a göre %50 tasarruf.',
      heroTitle: 'Türkiye İçin Çağrı Merkezi — Türkçe Müşteri Desteği',
      heroSubtitle: 'Türk pazarı için premium müşteri hizmeti. İstanbul\'dan %50 daha ucuz.',
    },
    fr: {
      title: 'Centre d\'appel Turquie | Service Client Turcophone | Call IT Dev',
      description: 'Centre d\'appel pour la Turquie : agents turcophones, CSAT 95%, dès 3 $/h. 50% d\'économies vs Istanbul.',
      heroTitle: 'Centre d\'appel pour la Turquie — Support turcophone',
      heroSubtitle: 'Service client premium en turc pour le marché turc.',
    },
  },

  scandinavia: {
    sv: {
      title: 'Callcenter Skandinavien | Nordisk Kundtjänst 24/7 | Call IT Dev',
      description: 'Callcenter för Skandinavien: nordisktalande agenter, CSAT 95%, från 3 $/h. 60% besparing. Nordisk kundtjänst.',
      heroTitle: 'Callcenter för Skandinavien — Nordisk Kundtjänst',
      heroSubtitle: 'Premium kundtjänst för den nordiska marknaden. 60% billigare.',
    },
    da: {
      title: 'Callcenter Skandinavien | Nordisk Kundeservice 24/7 | Call IT Dev',
      description: 'Callcenter til Skandinavien: nordisktalende agenter, CSAT 95%, fra 3 $/t. 60% besparelse. Nordisk kundeservice.',
      heroTitle: 'Callcenter til Skandinavien — Nordisk Kundeservice',
      heroSubtitle: 'Premium kundeservice for det nordiske marked. 60% billigere.',
    },
    fr: {
      title: 'Centre d\'appel Scandinavie | Service Client Nordique | Call IT Dev',
      description: 'Centre d\'appel pour la Scandinavie : agents nordiques, CSAT 95%, dès 3 $/h. Service client 24/7.',
      heroTitle: 'Centre d\'appel pour la Scandinavie — Support nordique',
      heroSubtitle: 'Service client premium pour le marché scandinave.',
    },
  },

  sweden: {
    sv: {
      title: 'Callcenter Sverige | Kundtjänst Outsourcing 24/7 | Call IT Dev',
      description: 'Callcenter för Sverige: svensktalande agenter, CSAT 95%, från 3 $/h. 55% besparing vs Stockholm.',
      heroTitle: 'Callcenter för Sverige — Svensktalande Kundtjänst',
      heroSubtitle: 'Premium kundtjänst på svenska. 55% billigare än Stockholm.',
    },
    fr: {
      title: 'Centre d\'appel Suède | Service Client Suédois | Call IT Dev',
      description: 'Centre d\'appel pour la Suède : agents suédophones, CSAT 95%, dès 3 $/h. 55% d\'économies vs Stockholm.',
      heroTitle: 'Centre d\'appel pour la Suède — Support suédois',
      heroSubtitle: 'Service client premium en suédois.',
    },
  },

  denmark: {
    da: {
      title: 'Callcenter Danmark | Kundeservice Outsourcing 24/7 | Call IT Dev',
      description: 'Callcenter til Danmark: dansktalende agenter, CSAT 95%, fra 3 $/t. 55% besparelse vs København.',
      heroTitle: 'Callcenter til Danmark — Dansktalende Kundeservice',
      heroSubtitle: 'Premium kundeservice på dansk. 55% billigere end København.',
    },
    fr: {
      title: 'Centre d\'appel Danemark | Service Client Danois | Call IT Dev',
      description: 'Centre d\'appel pour le Danemark : agents danophones, CSAT 95%, dès 3 $/h. 55% d\'économies vs Copenhague.',
      heroTitle: 'Centre d\'appel pour le Danemark — Support danois',
      heroSubtitle: 'Service client premium en danois.',
    },
  },

  brazil: {
    pt: {
      title: 'Call Center Brasil | Terceirização Atendimento ao Cliente 24/7 | Call IT Dev',
      description: 'Call center para o Brasil: agentes lusófonos, CSAT 95%, a partir de 3 $/h. 60% economia vs São Paulo. Orçamento gratuito.',
      heroTitle: 'Call Center para o Brasil — Suporte em Português',
      heroSubtitle: 'Atendimento ao cliente premium para o mercado brasileiro. 60% mais barato que São Paulo.',
    },
    es: {
      title: 'Call Center Brasil | Externalización Atención al Cliente | Call IT Dev',
      description: 'Call center para Brasil: agentes lusófonos, CSAT 95%, desde 3 $/h. 60% ahorro vs São Paulo.',
      heroTitle: 'Call Center para Brasil — Soporte en Portugués',
      heroSubtitle: 'Atención al cliente premium para el mercado brasileño.',
    },
    fr: {
      title: 'Centre d\'appel Brésil | Service Client Lusophone | Call IT Dev',
      description: 'Centre d\'appel pour le Brésil : agents lusophones, CSAT 95%, dès 3 $/h. 60% d\'économies vs São Paulo.',
      heroTitle: 'Centre d\'appel pour le Brésil — Support lusophone',
      heroSubtitle: 'Service client premium en portugais pour le marché brésilien.',
    },
  },

  japan: {
    ja: {
      title: '日本向けコールセンター | カスタマーサポート外注 24/7 | Call IT Dev',
      description: '日本向けコールセンター外注：日本語対応、CSAT 95%、3$/時間〜。東京比65%コスト削減。無料見積もり。',
      heroTitle: '日本向けコールセンター — 多言語カスタマーサポート',
      heroSubtitle: '日本市場向けプレミアムカスタマーサービス。東京より65%低コスト。',
    },
    fr: {
      title: 'Centre d\'appel Japon | Service Client Japonais | Call IT Dev',
      description: 'Centre d\'appel pour le Japon : support japonais, CSAT 95%, dès 3 $/h. 65% d\'économies vs Tokyo.',
      heroTitle: 'Centre d\'appel pour le Japon — Support japonais',
      heroSubtitle: 'Service client premium pour le marché japonais.',
    },
  },

  korea: {
    ko: {
      title: '한국 콜센터 | 고객 서비스 아웃소싱 24/7 | Call IT Dev',
      description: '한국 콜센터 아웃소싱: 한국어 지원, CSAT 95%, 시간당 3달러부터. 서울 대비 65% 비용 절감. 무료 견적.',
      heroTitle: '한국 콜센터 — 다국어 고객 지원',
      heroSubtitle: '한국 시장을 위한 프리미엄 고객 서비스. 서울보다 65% 저렴.',
    },
    fr: {
      title: 'Centre d\'appel Corée du Sud | Service Client Coréen | Call IT Dev',
      description: 'Centre d\'appel pour la Corée du Sud : support coréen, CSAT 95%, dès 3 $/h.',
      heroTitle: 'Centre d\'appel pour la Corée du Sud — Support coréen',
      heroSubtitle: 'Service client premium pour le marché coréen.',
    },
  },

  india: {
    fr: {
      title: 'Centre d\'appel Inde | Alternative BPO Premium | Call IT Dev',
      description: 'Alternative premium au BPO indien : agents multilingues, CSAT 95%, fuseau CET. Qualité nearshore vs offshore.',
      heroTitle: 'Centre d\'appel pour l\'Inde — Alternative Premium Nearshore',
      heroSubtitle: 'Alternative de qualité supérieure au BPO offshore indien.',
    },
  },

  australia: {
    fr: {
      title: 'Centre d\'appel Australie | Service Client Anglophone | Call IT Dev',
      description: 'Centre d\'appel pour l\'Australie : agents anglophones, couverture 24/7, CSAT 95%, dès 3 $/h.',
      heroTitle: 'Centre d\'appel pour l\'Australie — Support anglophone 24/7',
      heroSubtitle: 'Service client premium pour le marché australien.',
    },
  },

  mexico: {
    es: {
      title: 'Call Center México | Externalización Atención al Cliente | Call IT Dev',
      description: 'Call center para México: agentes hispanohablantes, CSAT 95%, desde 3 $/h. 55% ahorro vs Ciudad de México.',
      heroTitle: 'Call Center para México — Soporte Hispanohablante',
      heroSubtitle: 'Atención al cliente premium en español para el mercado mexicano.',
    },
    fr: {
      title: 'Centre d\'appel Mexique | Service Client Hispanophone | Call IT Dev',
      description: 'Centre d\'appel pour le Mexique : agents hispanophones, CSAT 95%, dès 3 $/h.',
      heroTitle: 'Centre d\'appel pour le Mexique — Support hispanophone',
      heroSubtitle: 'Service client premium en espagnol pour le marché mexicain.',
    },
  },

  colombia: {
    es: {
      title: 'Call Center Colombia | Externalización Servicio al Cliente | Call IT Dev',
      description: 'Call center para Colombia: agentes hispanohablantes, CSAT 95%, desde 3 $/h. Soporte 24/7.',
      heroTitle: 'Call Center para Colombia — Soporte Hispanohablante',
      heroSubtitle: 'Atención al cliente premium para el mercado colombiano.',
    },
    fr: {
      title: 'Centre d\'appel Colombie | Service Client Hispanophone | Call IT Dev',
      description: 'Centre d\'appel pour la Colombie : agents hispanophones, CSAT 95%, dès 3 $/h.',
      heroTitle: 'Centre d\'appel pour la Colombie — Support hispanophone',
      heroSubtitle: 'Service client premium pour le marché colombien.',
    },
  },

  argentina: {
    es: {
      title: 'Call Center Argentina | Externalización Servicio al Cliente | Call IT Dev',
      description: 'Call center para Argentina: agentes hispanohablantes, CSAT 95%, desde 3 $/h. Soporte 24/7.',
      heroTitle: 'Call Center para Argentina — Soporte Hispanohablante',
      heroSubtitle: 'Atención al cliente premium para el mercado argentino.',
    },
    fr: {
      title: 'Centre d\'appel Argentine | Service Client Hispanophone | Call IT Dev',
      description: 'Centre d\'appel pour l\'Argentine : agents hispanophones, CSAT 95%, dès 3 $/h.',
      heroTitle: 'Centre d\'appel pour l\'Argentine — Support hispanophone',
      heroSubtitle: 'Service client premium pour le marché argentin.',
    },
  },

  egypt: {
    ar: {
      title: 'مركز اتصال مصر | خدمة عملاء عربية 24/7 | Call IT Dev',
      description: 'مركز اتصال لمصر: وكلاء عرب أصليون، CSAT 95%، من 3 $/ساعة. دعم 24/7.',
      heroTitle: 'مركز اتصال لمصر — دعم عربي أصلي',
      heroSubtitle: 'خدمة عملاء متميزة بالعربية للسوق المصري.',
    },
    fr: {
      title: 'Centre d\'appel Égypte | Service Client Arabophone | Call IT Dev',
      description: 'Centre d\'appel pour l\'Égypte : agents arabophones, CSAT 95%, dès 3 $/h.',
      heroTitle: 'Centre d\'appel pour l\'Égypte — Support arabophone',
      heroSubtitle: 'Service client premium en arabe pour le marché égyptien.',
    },
  },

  qatar: {
    ar: {
      title: 'مركز اتصال قطر | خدمة عملاء متعددة اللغات | Call IT Dev',
      description: 'مركز اتصال لقطر: وكلاء عرب وإنجليز، CSAT 95%، من 3 $/ساعة. دعم 24/7.',
      heroTitle: 'مركز اتصال لقطر — دعم متعدد اللغات',
      heroSubtitle: 'خدمة عملاء متميزة للسوق القطري.',
    },
    fr: {
      title: 'Centre d\'appel Qatar | Service Client Multilingue | Call IT Dev',
      description: 'Centre d\'appel pour le Qatar : agents multilingues, CSAT 95%, dès 3 $/h.',
      heroTitle: 'Centre d\'appel pour le Qatar — Support multilingue',
      heroSubtitle: 'Service client premium pour le marché qatari.',
    },
  },

  // ============ EXPANDED COUNTRIES ============

  philippines: {
    fr: {
      title: 'Centre d\'appel Philippines | Alternative Premium | Call IT Dev',
      description: 'Alternative premium au BPO philippin : agents multilingues, CSAT 95%, fuseau CET. Qualité nearshore supérieure.',
      heroTitle: 'Centre d\'appel pour les Philippines — Alternative Nearshore Premium',
      heroSubtitle: 'Alternative de qualité supérieure au BPO offshore philippin.',
    },
    es: {
      title: 'Call Center Filipinas | Alternativa Premium BPO | Call IT Dev',
      description: 'Alternativa premium al BPO filipino: agentes multilingües, CSAT 95%, zona CET.',
      heroTitle: 'Call Center para Filipinas — Alternativa Premium Nearshore',
      heroSubtitle: 'Alternativa de calidad superior al BPO offshore filipino.',
    },
  },

  nigeria: {
    fr: {
      title: 'Centre d\'appel Nigeria | Service Client Anglophone Afrique | Call IT Dev',
      description: 'Centre d\'appel pour le Nigeria : agents anglophones, CSAT 95%, dès 3 $/h. Support 24/7 pour le marché nigérian.',
      heroTitle: 'Centre d\'appel pour le Nigeria — Support anglophone Afrique',
      heroSubtitle: 'Service client premium pour le marché nigérian.',
    },
    ar: {
      title: 'مركز اتصال نيجيريا | خدمة عملاء أفريقيا | Call IT Dev',
      description: 'مركز اتصال لنيجيريا: وكلاء ناطقون بالإنجليزية، CSAT 95%، من 3 $/ساعة.',
      heroTitle: 'مركز اتصال لنيجيريا — دعم أفريقي',
      heroSubtitle: 'خدمة عملاء متميزة للسوق النيجيري.',
    },
  },

  vietnam: {
    fr: {
      title: 'Centre d\'appel Vietnam | Service Client Asie | Call IT Dev',
      description: 'Centre d\'appel pour le Vietnam : agents multilingues, CSAT 95%, dès 3 $/h. Couverture Asie du Sud-Est.',
      heroTitle: 'Centre d\'appel pour le Vietnam — Support multilingue Asie',
      heroSubtitle: 'Service client premium pour le marché vietnamien.',
    },
  },

  greece: {
    fr: {
      title: 'Centre d\'appel Grèce | Service Client Hellénophone | Call IT Dev',
      description: 'Centre d\'appel pour la Grèce : agents hellénophones, CSAT 95%, dès 3 $/h. Support CET.',
      heroTitle: 'Centre d\'appel pour la Grèce — Support hellénophone',
      heroSubtitle: 'Service client premium pour le marché grec.',
    },
    de: {
      title: 'Call Center Griechenland | Kundenservice | Call IT Dev',
      description: 'Call Center für Griechenland: griechischsprachige Agenten, CSAT 95%, ab 3 $/h.',
      heroTitle: 'Call Center für Griechenland — Griechischsprachiger Support',
      heroSubtitle: 'Premium-Kundenservice für den griechischen Markt.',
    },
  },

  kenya: {
    fr: {
      title: 'Centre d\'appel Kenya | Service Client Afrique de l\'Est | Call IT Dev',
      description: 'Centre d\'appel pour le Kenya : agents anglophones, CSAT 95%, dès 3 $/h. Hub Afrique de l\'Est.',
      heroTitle: 'Centre d\'appel pour le Kenya — Support Afrique de l\'Est',
      heroSubtitle: 'Service client premium pour le marché kenyan.',
    },
    ar: {
      title: 'مركز اتصال كينيا | خدمة عملاء شرق أفريقيا | Call IT Dev',
      description: 'مركز اتصال لكينيا: وكلاء ناطقون بالإنجليزية، CSAT 95%، من 3 $/ساعة.',
      heroTitle: 'مركز اتصال لكينيا — دعم شرق أفريقيا',
      heroSubtitle: 'خدمة عملاء متميزة للسوق الكيني.',
    },
  },

  thailand: {
    fr: {
      title: 'Centre d\'appel Thaïlande | Service Client Asie du Sud-Est | Call IT Dev',
      description: 'Centre d\'appel pour la Thaïlande : agents multilingues, CSAT 95%, dès 3 $/h.',
      heroTitle: 'Centre d\'appel pour la Thaïlande — Support multilingue',
      heroSubtitle: 'Service client premium pour le marché thaïlandais.',
    },
  },

  malaysia: {
    fr: {
      title: 'Centre d\'appel Malaisie | Service Client Multilingue | Call IT Dev',
      description: 'Centre d\'appel pour la Malaisie : agents multilingues, CSAT 95%, dès 3 $/h.',
      heroTitle: 'Centre d\'appel pour la Malaisie — Support multilingue Asie',
      heroSubtitle: 'Service client premium pour le marché malaisien.',
    },
  },

  ukraine: {
    fr: {
      title: 'Centre d\'appel Ukraine | Service Client Europe de l\'Est | Call IT Dev',
      description: 'Centre d\'appel pour l\'Ukraine : agents multilingues, CSAT 95%, dès 3 $/h.',
      heroTitle: 'Centre d\'appel pour l\'Ukraine — Support Europe de l\'Est',
      heroSubtitle: 'Service client premium pour le marché ukrainien.',
    },
    de: {
      title: 'Call Center Ukraine | Kundenservice Osteuropa | Call IT Dev',
      description: 'Call Center für Ukraine: mehrsprachige Agenten, CSAT 95%, ab 3 $/h.',
      heroTitle: 'Call Center für die Ukraine — Osteuropa-Support',
      heroSubtitle: 'Premium-Kundenservice für den ukrainischen Markt.',
    },
    pl: {
      title: 'Call Center Ukraina | Obsługa Klienta Europa Wschodnia | Call IT Dev',
      description: 'Call center dla Ukrainy: wielojęzyczni agenci, CSAT 95%, od 3 $/h.',
      heroTitle: 'Call Center dla Ukrainy — Wsparcie Europa Wschodnia',
      heroSubtitle: 'Premium obsługa klienta dla rynku ukraińskiego.',
    },
  },

  russia: {
    ru: {
      title: 'Колл-центр Россия | Аутсорсинг обслуживания клиентов 24/7 | Call IT Dev',
      description: 'Колл-центр для России: русскоговорящие агенты, CSAT 95%, от 3 $/ч. Многоязычная поддержка.',
      heroTitle: 'Колл-центр для России — Русскоязычная поддержка',
      heroSubtitle: 'Премиум обслуживание клиентов на русском языке.',
    },
    fr: {
      title: 'Centre d\'appel Russie | Service Client Russophone | Call IT Dev',
      description: 'Centre d\'appel pour la Russie : agents russophones, CSAT 95%, dès 3 $/h.',
      heroTitle: 'Centre d\'appel pour la Russie — Support russophone',
      heroSubtitle: 'Service client premium en russe.',
    },
  },

  china: {
    zh: {
      title: '中国呼叫中心 | 客服外包 24/7 | Call IT Dev',
      description: '中国呼叫中心外包：多语种座席，CSAT 95%，3美元/小时起。全天候客户支持。',
      heroTitle: '中国呼叫中心 — 多语种客户支持',
      heroSubtitle: '为中国市场提供优质客户服务。',
    },
    fr: {
      title: 'Centre d\'appel Chine | Service Client Sinophone | Call IT Dev',
      description: 'Centre d\'appel pour la Chine : agents sinophones, CSAT 95%, dès 3 $/h.',
      heroTitle: 'Centre d\'appel pour la Chine — Support sinophone',
      heroSubtitle: 'Service client premium pour le marché chinois.',
    },
  },

  'south-africa': {
    fr: {
      title: 'Centre d\'appel Afrique du Sud | Service Client Anglophone | Call IT Dev',
      description: 'Centre d\'appel pour l\'Afrique du Sud : agents anglophones, CSAT 95%, dès 3 $/h. Hub Afrique australe.',
      heroTitle: 'Centre d\'appel pour l\'Afrique du Sud — Support anglophone',
      heroSubtitle: 'Service client premium pour le marché sud-africain.',
    },
    ar: {
      title: 'مركز اتصال جنوب أفريقيا | خدمة عملاء | Call IT Dev',
      description: 'مركز اتصال لجنوب أفريقيا: وكلاء ناطقون بالإنجليزية، CSAT 95%، من 3 $/ساعة.',
      heroTitle: 'مركز اتصال لجنوب أفريقيا — دعم جنوب أفريقي',
      heroSubtitle: 'خدمة عملاء متميزة للسوق الجنوب أفريقي.',
    },
  },

  'saudi-arabia-expanded': {
    ar: {
      title: 'مركز اتصال السعودية KSA | خدمة عملاء رؤية 2030 | Call IT Dev',
      description: 'مركز اتصال للمملكة العربية السعودية: وكلاء عرب أصليون، CSAT 95%، متوافق مع رؤية 2030.',
      heroTitle: 'مركز اتصال للسعودية KSA — دعم رؤية 2030',
      heroSubtitle: 'خدمة عملاء متميزة متوافقة مع رؤية المملكة 2030.',
    },
    fr: {
      title: 'Centre d\'appel KSA | Service Client Vision 2030 | Call IT Dev',
      description: 'Centre d\'appel pour l\'Arabie Saoudite KSA : agents arabophones, CSAT 95%, Vision 2030.',
      heroTitle: 'Centre d\'appel pour KSA — Support Vision 2030',
      heroSubtitle: 'Service client pour le marché saoudien.',
    },
  },
};

// Merge wave 2-11 translations (deep merge per country)
import { COUNTRY_SEO_I18N_WAVE3 } from './country-seo-i18n-wave3';
import { COUNTRY_SEO_I18N_WAVE4 } from './country-seo-i18n-wave4';
import { COUNTRY_SEO_I18N_WAVE5 } from './country-seo-i18n-wave5';
import { COUNTRY_SEO_I18N_WAVE6 } from './country-seo-i18n-wave6';
import { COUNTRY_SEO_I18N_WAVE7 } from './country-seo-i18n-wave7';
import { COUNTRY_SEO_I18N_WAVE8 } from './country-seo-i18n-wave8';
import { COUNTRY_SEO_I18N_WAVE9 } from './country-seo-i18n-wave9';
import { COUNTRY_SEO_I18N_WAVE10 } from './country-seo-i18n-wave10';
import { COUNTRY_SEO_I18N_WAVE11 } from './country-seo-i18n-wave11';
import { COUNTRY_SEO_I18N_WAVE12 } from './country-seo-i18n-wave12';
import { COUNTRY_SEO_I18N_WAVE13 } from './country-seo-i18n-wave13';
import { COUNTRY_SEO_I18N_WAVE14 } from './country-seo-i18n-wave14';
import { COUNTRY_SEO_I18N_WAVE15 } from './country-seo-i18n-wave15';
import { COUNTRY_SEO_I18N_WAVE16 } from './country-seo-i18n-wave16';
import { COUNTRY_SEO_I18N_WAVE17 } from './country-seo-i18n-wave17';
import { COUNTRY_SEO_I18N_WAVE18 } from './country-seo-i18n-wave18';
import { COUNTRY_SEO_I18N_WAVE19 } from './country-seo-i18n-wave19';
import { COUNTRY_SEO_I18N_WAVE20 } from './country-seo-i18n-wave20';
import { COUNTRY_SEO_I18N_WAVE21 } from './country-seo-i18n-wave21';
import { COUNTRY_SEO_I18N_WAVE22 } from './country-seo-i18n-wave22';
import { COUNTRY_SEO_I18N_WAVE23 } from './country-seo-i18n-wave23';
import { COUNTRY_SEO_I18N_WAVE24 } from './country-seo-i18n-wave24';
import { COUNTRY_SEO_I18N_WAVE25 } from './country-seo-i18n-wave25';
import { COUNTRY_SEO_I18N_WAVE26 } from './country-seo-i18n-wave26';
import { COUNTRY_SEO_I18N_WAVE27 } from './country-seo-i18n-wave27';
import { COUNTRY_SEO_I18N_WAVE28 } from './country-seo-i18n-wave28';
import { COUNTRY_SEO_I18N_WAVE29 } from './country-seo-i18n-wave29';
import { COUNTRY_SEO_I18N_WAVE30 } from './country-seo-i18n-wave30';
import { COUNTRY_SEO_I18N_WAVE31 } from './country-seo-i18n-wave31';
import { COUNTRY_SEO_I18N_WAVE32 } from './country-seo-i18n-wave32';

import { COUNTRY_SEO_I18N_WAVE33 } from './country-seo-i18n-wave33';
import { COUNTRY_SEO_I18N_WAVE34 } from './country-seo-i18n-wave34';
import { COUNTRY_SEO_I18N_WAVE35_40 } from './country-seo-i18n-wave35';
import { COUNTRY_SEO_I18N_WAVE36 } from './country-seo-i18n-wave36';
import { COUNTRY_SEO_I18N_WAVE37 } from './country-seo-i18n-wave37';
import { COUNTRY_SEO_I18N_WAVE38 } from './country-seo-i18n-wave38';
for (const wave of [COUNTRY_SEO_I18N_WAVE2, COUNTRY_SEO_I18N_WAVE3, COUNTRY_SEO_I18N_WAVE4, COUNTRY_SEO_I18N_WAVE5, COUNTRY_SEO_I18N_WAVE6, COUNTRY_SEO_I18N_WAVE7, COUNTRY_SEO_I18N_WAVE8, COUNTRY_SEO_I18N_WAVE9, COUNTRY_SEO_I18N_WAVE10, COUNTRY_SEO_I18N_WAVE11, COUNTRY_SEO_I18N_WAVE12, COUNTRY_SEO_I18N_WAVE13, COUNTRY_SEO_I18N_WAVE14, COUNTRY_SEO_I18N_WAVE15, COUNTRY_SEO_I18N_WAVE16, COUNTRY_SEO_I18N_WAVE17, COUNTRY_SEO_I18N_WAVE18, COUNTRY_SEO_I18N_WAVE19, COUNTRY_SEO_I18N_WAVE20, COUNTRY_SEO_I18N_WAVE21, COUNTRY_SEO_I18N_WAVE22, COUNTRY_SEO_I18N_WAVE23, COUNTRY_SEO_I18N_WAVE24, COUNTRY_SEO_I18N_WAVE25, COUNTRY_SEO_I18N_WAVE26, COUNTRY_SEO_I18N_WAVE27, COUNTRY_SEO_I18N_WAVE28, COUNTRY_SEO_I18N_WAVE29, COUNTRY_SEO_I18N_WAVE30, COUNTRY_SEO_I18N_WAVE31, COUNTRY_SEO_I18N_WAVE32, COUNTRY_SEO_I18N_WAVE33, COUNTRY_SEO_I18N_WAVE34, COUNTRY_SEO_I18N_WAVE35_40, COUNTRY_SEO_I18N_WAVE36, COUNTRY_SEO_I18N_WAVE37, COUNTRY_SEO_I18N_WAVE38]) {
  for (const [countryKey, translations] of Object.entries(wave)) {
    if (COUNTRY_SEO_I18N[countryKey]) {
      Object.assign(COUNTRY_SEO_I18N[countryKey], translations);
    } else {
      COUNTRY_SEO_I18N[countryKey] = translations;
    }
  }
}

/**
 * Get localized SEO data for a country page
 * Falls back to English (data from country-landing-pages.ts) if no i18n exists
 */
export function getCountrySEO(countryKey: string, language: string): CountrySEO | null {
  const country = COUNTRY_SEO_I18N[countryKey];
  if (!country) return null;
  return country[language] || null;
}
